package com.milta.core.localization

/**
 * MIL-97 — RoutaR optimization counter per account: exhausted messages of the optimization buttons
 * (mobile + web), "Mes optimisations" (mobile Profile + web Paramètres) and the Admin "Limites d'usage" / "Fiche
 * utilisateur" pages. Placeholders between braces (`{remaining}`, `{limit}`, `{date}`...) are
 * replaced in code.
 */
data class OptimizationQuotaStrings(
    // ── Optimization buttons (R10, R11) ──
    val exhaustedTitle: String,
    /** M1 — "Retour le {date}. Vous pouvez créer la tournée sans optimisation : …" */
    val exhaustedCreateRouteMessage: String,
    /** E1 — "Retour à {limit} optimisations le {date}. Le lot peut être créé sans optimisation : …" */
    val exhaustedCreateBatchMessage: String,
    /** R11 — "Quota d'optimisations atteint — remise à zéro le {date}" */
    val quotaReachedResetOn: String,
    /** M1, after R11 — appended to [quotaReachedResetOn]. */
    val routeCreatedInEnteredOrder: String,
    /** M1 Driver mode, quota at 0 before the tap: creation stays possible, unoptimized. */
    val routeWillBeCreatedInEnteredOrder: String,
    /** E1, after R11 — body under [quotaReachedResetOn]. */
    val batchCreatedInEnteredOrder: String,
    /** E1 — the batch is created without optimization. */
    val sendWithoutOptimization: String,
    // ── Batch result / preview (E2, M1b) ──
    val batchNotOptimizedTitle: String,
    val batchNotOptimizedMessage: String,
    /** M1b — single-line banner. */
    val batchNotOptimizedMobileMessage: String,
    val reoptimizeAction: String,
    /** E2 — "Ré-optimisation indisponible : quota atteint, retour le {date}. …" */
    val reoptimizeUnavailableWeb: String,
    /** M1b — "Ré-optimisation indisponible jusqu'au {date} (quota atteint). L'envoi reste possible." */
    val reoptimizeUnavailableMobile: String,
    // ── Route in progress (M2) ──
    val stopAddedAtEndTitle: String,
    /** "Quota d'optimisations atteint — remise à zéro le {date}. L'ordre des autres arrêts ne change pas." */
    val stopAddedAtEndMessage: String,
    /** "Quota d'optimisations atteint, retour le {date}" */
    val reoptimizeExhaustedHint: String,
    // ── "Mes optimisations" (M3, E3) ──
    val myOptimizationsTitle: String,
    val remainingForPeriod: String,
    /** "restantes", after "3 / 100". */
    val remainingSuffix: String,
    val resetLabel: String,
    val resetAtRenewal: String,
    val driverExplanation: String,
    val enterpriseExplanation: String,
    val webCounterNotice: String,
    val historyTitle: String,
    val columnDate: String,
    val columnAction: String,
    val columnTarget: String,
    val columnOutcome: String,
    val actionOptimizeRoute: String,
    val actionCreateRouteBatch: String,
    val actionReoptimizeRouteBatch: String,
    val actionReoptimizeOnStopAdded: String,
    val actionReoptimizeInPlace: String,
    /** POST routes/me ("Créer / Optimiser ma tournée", Driver mode). */
    val actionCreateOwnRoute: String,
    val outcomeCounted: String,
    val outcomeRefunded: String,
    val outcomeRefundedLong: String,
    /** Mobile row suffix of a refunded optimization. */
    val refundedNotCounted: String,
    /** "Tournée du {date}" */
    val targetRoute: String,
    /** "Lot du {date}" */
    val targetBatch: String,
    val historyLoadingMore: String,
    /** "Fin de l'historique — {count} lignes" */
    val historyEnd: String,
    val historyEmpty: String,
    val historyLoadError: String,
    val retryAction: String,
    val loading: String,
    val errorNoConnection: String,
    val errorGeneric: String,
    // ── Admin "Limites d'usage" (A1) ──
    val tabOptimizations: String,
    val tabAiTokens: String,
    val comingSoon: String,
    val limitsDescription: String,
    val columnTrial: String,
    val columnPaid: String,
    val rowEnterprise: String,
    val rowDriver: String,
    val fieldRequired: String,
    val fieldMustBePositive: String,
    val limitsFootnote: String,
    /** "Limites enregistrées. Entreprise : {enterpriseTrial} essai · {enterprisePaid} payant. Chauffeur : …" */
    val limitsSaved: String,
    val limitsRejected: String,
    val saveAction: String,
    // ── Admin "Fiche utilisateur" (A2) ──
    /** Admin "Utilisateurs" — the button of every row opening the account's details. */
    val detailsAction: String,
    /** Admin — header subtitle of the account's details page. */
    val accountDetailsTitle: String,
    val sectionIdentity: String,
    val accountTypeLabel: String,
    val kindEnterpriseOwner: String,
    val kindEnterpriseDriver: String,
    val kindIndependentDriver: String,
    val withoutOrganisation: String,
    val subscriptionLabel: String,
    val viaOrganisation: String,
    val subscriptionTrial: String,
    val subscriptionPaid: String,
    val appliedLimitLabel: String,
    val limitKindEnterprise: String,
    val limitKindDriver: String,
    val enterpriseInTrialSuffix: String,
    val resetSourceOwn: String,
    /** "Renouvellement de {enterprise}" */
    val resetSourceEnterprise: String,
    val exhaustedBadge: String,
    val manuallyAdjustedBadge: String,
    val replaceLabel: String,
    val replaceAction: String,
    val replaceHelp: String,
    /** "Ne modifie que le compteur de {name}. Celui de l'Entreprise et des autres chauffeurs ne change pas." */
    val replaceHelpEnterpriseDriver: String,
    val replaceInvalid: String,
    val replaceRejected: String,
    /** "Maximum 100 000" — the server's cap on a replaced remaining count. */
    val replaceMaximum: String,
    /** "Nombre restant remplacé : {from} → {to}. Pour la période en cours. Le {date}, retour à la limite globale ({limit})." */
    val replacedBanner: String,
    val adminHistoryTitle: String,
    val accountNotFound: String,
)
